import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { addNewProjectAPI } from '../../apiConfig/apiCall';
import { getCertificationList } from '../../slices/ManageCompliance/manageComplianceSlice';
import { getProjectCertifications } from '../../slices/ManageClient/thunk';

export default function AddProject() {
    const dispatch = useDispatch();
    const params = useParams();
    const { isLoading, isError, data } = useSelector(state => state.manageCompliance);

    const [submittingForm, setSubmittingForm] = useState(false); // submittingForm state

    const [formData, setFormData] = useState({
        clientId: params.id,
        startDate: moment().format('YYYY-MM-DD'), // Current date as default startDate
        dueDate: moment().add(1, 'year').format('YYYY-MM-DD'), // One year later as default dueDate
        versionIds: []
    });

    // Function to validate form
    const validateForm = () => {
        if (!formData.startDate) {
            toast.error('Start Date is required.');
            return false;
        }
        if (!formData.dueDate) {
            toast.error('Due Date is required.');
            return false;
        }
        const startDate = moment(formData.startDate);
        const dueDate = moment(formData.dueDate);

        if (dueDate.diff(startDate, 'days') < 30) {
            toast.error('Due Date must be at least 1 month after Start Date.');
            return false;
        }
        if (formData.versionIds.length === 0) {
            toast.error('At least one subscription must be selected.');
            return false;
        }
        return true;
    };

    const handleStartDateChange = (date) => {
        const newStartDate = moment(date[0]).format('YYYY-MM-DD');
        const newDueDate = moment(newStartDate).add(1, 'year').format('YYYY-MM-DD');

        setFormData({
            ...formData,
            startDate: newStartDate,
            dueDate: moment(formData.dueDate).isBefore(moment(newStartDate).add(30, 'days'))
                ? newDueDate
                : formData.dueDate,
        });
    };

    const handleDueDateChange = (date) => {
        setFormData({ ...formData, dueDate: moment(date[0]).format('YYYY-MM-DD') });
    };

    const handleCheckboxChange = (event, id) => {
        const { checked } = event.target;
        let newVersionIds = [...formData.versionIds];

        if (checked) {
            newVersionIds.push(id);
        } else {
            newVersionIds = newVersionIds.filter(vId => vId !== id);
        }

        setFormData({ ...formData, versionIds: newVersionIds });
    };

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();

            if (!validateForm()) return;

            if (submittingForm) return;

            const payload = {
                clientId: formData.clientId,
                startDate: formData.startDate,
                dueDate: formData.dueDate,
                versionIds: formData.versionIds.join(',')
            };

            setSubmittingForm(true);

            const apiResponse = await addNewProjectAPI(payload);
            if (apiResponse.status === 201) {
                toast.success(apiResponse.data);
                dispatch(getProjectCertifications(params.id));
                handleReset();
            } else if (apiResponse.status === 302) {
                toast.error(apiResponse.data);
            }
        } catch (error) {
            console.error('Error sending data:', error);
            toast.error('An error occurred while adding the project');
        } finally {
            setSubmittingForm(false);
        }
    };

    const handleReset = () => {
        setFormData({
            clientId: params.id,
            startDate: moment().format('YYYY-MM-DD'),
            dueDate: moment().add(1, 'year').format('YYYY-MM-DD'),
            versionIds: []
        });
    };

    useEffect(() => {
        dispatch(getCertificationList());
    }, [dispatch]);

    return (
        <>
            <Row>
                <Col lg={12} className='pe-1 ps-1'>
                    <Card className='mb-2'>
                        <CardHeader className='fs-4 py-2 fw-medium'>Add New Project</CardHeader>
                        <CardBody className="p-4">
                            <Form>
                                <Row className="gy-3">
                                    <Col lg={6}>
                                        <div>
                                            <Label htmlFor="startDate" className="form-label">Start Date</Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{ dateFormat: "Y-m-d" }}
                                                onChange={handleStartDateChange}
                                                value={formData.startDate ? new Date(formData.startDate) : ''}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={6}>
                                        <div>
                                            <Label htmlFor="dueDate" className="form-label">Due Date</Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{ dateFormat: "Y-m-d" }}
                                                onChange={handleDueDateChange}
                                                value={formData.dueDate ? new Date(formData.dueDate) : ''}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <h5 className='text-danger pb-2'>Select client's subscription</h5>
                                        {data && data.map((certificate, key) => (
                                            <div className="form-check mb-2" key={key}>
                                                <Input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`certificate-${key}`}
                                                    onChange={(e) => handleCheckboxChange(e, certificate.id)}
                                                    checked={formData.versionIds.includes(certificate.id)}
                                                />
                                                <Label className="form-check-label" htmlFor={`certificate-${key}`}>
                                                    {certificate.certificationType} {certificate.certificationVersion}
                                                </Label>
                                            </div>
                                        ))}
                                    </Col>

                                    <Col lg={12}>
                                        {
                                            submittingForm ? <Button color="primary" className="me-2" type='button' disabled>
                                                <Spinner size="sm"> Loading... </Spinner> </Button> : <Button color="primary" type="submit" className="me-2" disabled={submittingForm} onClick={handleSubmit}> Add  </Button>
                                        }
                                        <Button type="button" color="secondary" onClick={handleReset} disabled={submittingForm}>Reset</Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
}